/**
 * Meeting Recaps section — affiche les recaps de réunions attachés au dossier.
 * Alimenté par le pipeline Meeting → CRM (Sprint 10.X).
 */
import { useEffect, useState } from "react";
import { Video, Users, Clock, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "@/lib/api";

const NAVY = "#0a2540";
const CHAMPAGNE = "#c9a34e";
const SLATE = "#6b7c93";
const INK = "#0a1a2c";

const SOURCE_LABELS = {
  zoom: "Zoom", teams: "Microsoft Teams", meet: "Google Meet",
  youtube: "YouTube", meeting: "Réunion",
};

export function DossierMeetingRecaps({ dossierId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!dossierId) return;
    api.get(`/knowledge-ingestion/dossiers/${dossierId}/notes`)
       .then(r => setNotes(r.data.notes || []))
       .catch(() => setNotes([]))
       .finally(() => setLoading(false));
  }, [dossierId]);

  if (loading) return null;

  return (
    <section className="mt-8" data-testid="dossier-recaps-section">
      <div className="rounded-md border border-[#E4E1D8] bg-white p-5">
        <div className="flex items-center gap-2 mb-4">
          <Video size={16} style={{ color: CHAMPAGNE }} />
          <h2 className="text-lg font-serif" style={{ color: NAVY }}>
            Recaps de réunions
          </h2>
          <span className="text-xs px-2 py-1 rounded-full"
                  style={{ background: "#0a254011", color: SLATE }}>
            {notes.length}
          </span>
        </div>

        {notes.length === 0 ? (
          <p className="text-sm" style={{ color: SLATE }}>
            Aucun recap pour ce client. Ingérez une réunion Zoom/Teams/Meet
            via le Knowledge Ingestion Center avec l'ID de ce dossier pour y
            faire apparaître le résumé exécutif automatique.
          </p>
        ) : (
          <div className="space-y-3">
            {notes.map(n => (
              <RecapCard key={n.id} note={n}
                          expanded={expanded === n.id}
                          onToggle={() => setExpanded(expanded === n.id ? null : n.id)} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function RecapCard({ note, expanded, onToggle }) {
  const source = SOURCE_LABELS[note.meeting_source] || "Réunion";
  const date = note.created_at
    ? new Date(note.created_at).toLocaleString("fr-CA",
                { dateStyle: "long", timeStyle: "short" })
    : "";
  return (
    <div className="rounded-lg border p-4"
          style={{ borderColor: "#0a254022" }}
          data-testid={`recap-${note.id}`}>
      <div className="flex justify-between items-start gap-3">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wide mb-1"
                style={{ color: SLATE }}>
            {source} · {date}
          </div>
          <h3 className="font-semibold" style={{ color: INK }}>
            {note.title}
          </h3>
          {!!note.participants?.length && (
            <div className="flex items-center gap-1 text-xs mt-1"
                  style={{ color: SLATE }}>
              <Users size={12} />
              {note.participants.join(", ")}
            </div>
          )}
        </div>
        <button onClick={onToggle}
                  data-testid={`recap-toggle-${note.id}`}
                  className="p-1 rounded hover:bg-[#0a254011]"
                  style={{ color: NAVY }}>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 p-3 rounded whitespace-pre-wrap text-sm leading-relaxed"
              style={{ background: "#F7F1E1", color: INK }}
              data-testid={`recap-body-${note.id}`}>
          {note.body_md}
        </div>
      )}

      {!!note.tags?.length && (
        <div className="mt-2 flex flex-wrap gap-1">
          {note.tags.filter(t => !t.startsWith("participant:")).slice(0, 6).map((t, i) => (
            <span key={i} className="text-xs px-2 py-0.5 rounded"
                    style={{ background: "#0a254008", color: SLATE }}>
              #{t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
